import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import Divider from '@mui/joy/Divider';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';
import Drawer from '@mui/joy/Drawer';
import ModalClose from '@mui/joy/ModalClose';
import AccordionGroup from '@mui/joy/AccordionGroup';
import Button from '@mui/joy/Button';

// import { CollabProps } from '../stores/collab_store';
import { getIcon, pluralize } from 'utils/misc';

import useUserIdentity from 'hooks/useUserIdentity';
import useEmail from 'hooks/useEmail';
import useFileAssociation from 'hooks/useFileAssociation';
import { EmailProps as EmailType, EmailHistoryType } from 'stores/email_store';
import { DateOrTime } from './Date';
import IdentityAvatar from './IdentityAvatar';
import FileItem from './FileItem';
import useWorkKey from 'hooks/useWorkKey';
import FancyIcon from './FancyIcon';
import { Success } from './FancyToast';

function EmailDetail({ email }: { email: EmailType }) {
    const { fetchListByAssociation, getListByAssociation, items } = useFileAssociation();
    const [fileAssociations, setFileAssociations] =
        useState(getListByAssociation('email', email.uuid));
    const { currentIdentity } = useUserIdentity();
    const workKey = useWorkKey();
    const [confirmingResend, setConfirmingResend] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([] as EmailHistoryType[]);
    const { resendEmail, fetchHistory } = useEmail();

    useEffect(() => {
        fetchListByAssociation(currentIdentity?.uuid, "email", email.uuid)
            .then((fetchedItems) => {
                // console.log("Fetched items", fetchedItems);
                setFileAssociations(fetchedItems);
            });
    }, [email.uuid, currentIdentity?.uuid]);

    useEffect(() => {
        setFileAssociations(getListByAssociation('email', email.uuid));
        // console.log("File association items", items);
    }, [items.length]);

    function loadHistory() {
        if(!workKey) {
            return;
        }

        setLoadingHistory(true);
        fetchHistory(workKey, email.uuid)
            .then((data) => {
                console.log("History", data);
                setShowHistory(true);
                setHistory(data);
                setLoadingHistory(false);
            });
        console.log("Load history");
    }

    function resend() {
        if(!workKey) {
            console.error("No identity loaded");
            return;
        }
        resendEmail(workKey, email.uuid).then(() => {
            setConfirmingResend(false);
            Success("Email resent");
        });
        console.log("Resend email", email);
    }


    function iconName(event: string) {
        if(event == 'delivered') {
            return 'Check';
        }
        else if(event == 'processed') {
            return 'Settings';
        }
        else if(event == 'bounce') {
            return 'CancelScheduleSend';
        }
        else if(event == 'open') {
            return 'Drafts';
        }
        else if(event == 'dropped') {
            return 'Alarm';
    }
        else if(event == 'click') {
            return 'AdsClick';
        }
        else if(event == 'spamreport') {
            return 'Alarm';
        }
        else if(event == 'unsubscribe') {
            return 'Alarm';
        }
        else if(event == 'deferred') {
            return 'ScheduleSend';
        }
        else {
            return 'Alarm'
        }
    }

    return (
        <Stack
            direction="column"
            spacing={2}
            padding={2}
        >

            <Stack>
                <Typography level="title-lg">
                {email.subject}
                </Typography>
                <Typography level="body-sm">
                    <IdentityAvatar interactive={false} size="inline" identityKey={email.identity} />
                </Typography>
                <Typography level="body-sm">
                    To: {email.to_email}
                </Typography>
                <Typography level="body-sm">
                    From: {email.from_email}
                </Typography>

                <Typography level="body-sm">
                    <DateOrTime ts={email.date_created} /> ({email.status})
                </Typography>
            </Stack>

            <Divider />

            <Stack spacing={1}>
            <Typography level="body-sm">
                {email.content}
            </Typography>
            <Divider />

            <Typography level="body-sm">
                {pluralize(fileAssociations.length, 'Attachment')}
            </Typography>

            {fileAssociations.map((f) =>
                <FileItem key={f.key} association={f} />
            )
            }
            </Stack>

            <Stack>

            <Divider />
            { !showHistory ?
            <Button variant="plain" onClick={loadHistory}>
            {loadingHistory ? 'Loading...' : 'Show History'}
            </Button>
                :
                <Button variant="plain" onClick={() => setShowHistory(false)}>
            {loadingHistory ? 'Loading...' : 'Hide History'}
            </Button>

            }
            { showHistory &&
            <Stack divider={<Divider />} spacing={1} pt={2}>
            { history.map((h) =>
                <Stack key={h.key} spacing={1}>
                <Stack spacing={0.5}>
                <Typography level="body-sm" startDecorator={<FancyIcon name="DayOnesEmail" />} >
                    {h.addr}
                    </Typography>
                    <Typography level="body-sm" startDecorator={<FancyIcon name={iconName(h.event)} />} >
                    {h.event}
                    </Typography>

                </Stack>
                    <Typography level="body-xs">
                         <DateOrTime ts={h.date_created} />
                    </Typography>
                </Stack>
            )
            }
            </Stack>
            }
            </Stack>


            { currentIdentity.role == 'assistant' &&
            <>
            <Divider />
            <Stack >
                {confirmingResend &&
                    <Stack  justifyContent="center" alignItems="center">
                        <Typography level="body-sm" color="warning">
                            Are you sure you want to resend this email?
                        </Typography>
                        <Button variant="plain" onClick={resend}>Yes, Resend Email</Button>
                    </Stack>

                }
                <Stack direction="row" justifyContent="center">
                    <Button variant="plain" onClick={() => setConfirmingResend(!confirmingResend)}>
                        {confirmingResend ? 'Cancel' : 'Resend Email'}
                    </Button>
                </Stack>
                </Stack>
                </>
            }
        </Stack>
    );
}

function EmailSidebarItem({ email, onClick }: { email: EmailType, onClick?: (email: EmailType) => void }) {
    return (
        <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => onClick && onClick(email)}
        >
            <Stack
                direction="column"
                spacing={0}
            >
                <Typography level="body-sm">
                    {email.subject}
                </Typography>
                <Typography level="body-xs">
                    {email.to_email}
                    </Typography>
                <Typography level="body-xs">
                    {pluralize(email.files.length, "Attachment")}
                </Typography>
            </Stack>
            <Typography level="body-xs">
                <DateOrTime ts={email.date_created} /> ({email.status})
            </Typography>
        </Stack>
    );
}

const CollabWorkSpaceSidebarEmail = observer(({ associated_type, associated_uuid }: { associated_type: string, associated_uuid: string }) => {
    const { fetchList, getEmailByAssociation } = useEmail();
    const workKey = useWorkKey();
    const [loaded, setLoaded] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const [selectedEmail, setSelectedEmail] = useState<EmailType | null>(null);
    const EmailIcon = getIcon('DayOnesEmail');

    const emails = getEmailByAssociation(associated_type, associated_uuid);

    function loader() {
        console.log("LOADER", associated_type, associated_uuid, workKey);
        if (associated_type && associated_uuid && workKey) {
            setLoaded(true);
            fetchList(workKey, associated_type, associated_uuid);
            console.log("OKOKOK");
        }
    }

    useEffect(() => {
        if (!loaded) {
            loader();
        }
    }, [loader, loaded, setLoaded]);

    return (
        <>
            <AccordionGroup size="lg">
                <Accordion
                    expanded={expanded}
                    onChange={() => setExpanded(!expanded)}
                >
                    <AccordionSummary>
                        <EmailIcon />
                        <Typography level="body-sm">
                            {pluralize(emails?.length, 'Email')}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack
                            spacing={1}
                            divider={<Divider />}
                        >
                            <Stack
                                justifyContent="center"
                                alignItems="center"
                                direction="row"
                                spacing={1}>

                            </Stack>
                            {emails &&
                                emails.map((c) =>
                                    <EmailSidebarItem key={c.key} email={c} onClick={setSelectedEmail} />
                                )
                            }

                        </Stack>
                    </AccordionDetails>
                </Accordion>
            </AccordionGroup>
            <Drawer

                open={selectedEmail !== null}
                onClose={() => setSelectedEmail(null)}>
                <Stack>
                    <ModalClose />

                    {selectedEmail &&
                        <EmailDetail email={selectedEmail} />
                    }
                </Stack>
            </Drawer>
        </>
    );
});

export default CollabWorkSpaceSidebarEmail;
